<template>
  <div class="page-container bg-gray-50">
    <!-- Main Layout -->
    <div class="main-layout md:mx-[20px] md:mt-[25px] md:mb-[25px] md:shadow-lg md:rounded-[5px] md:overflow-hidden">
      <!-- Sidebar -->
      <aside
        class="sidebar md:rounded-[5px]"
        :class="{
          'sidebar-collapsed': !isSidebarOpen,
          'sidebar-mobile-open': isSidebarOpen && isMobile,
          'sidebar-mobile-hidden': !isSidebarOpen && isMobile,
        }"  
      >
        <!-- Sidebar Header -->
        <div class="sidebar-header">
          <h2 v-show="isSidebarOpen" class="sidebar-title" style="margin-left: 10px;">
            {{ dashboardName }}
          </h2>
          <button
            class="sidebar-toggle"
            @click="toggleSidebar"
            :aria-label="isSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'"
          >
            <i class="fas fa-bars"></i>
          </button>
        </div>

        <!-- Navigation Menu -->
        <nav class="sidebar-nav">
          <template v-for="item in filteredMenuItems" :key="item.id">
            <!-- Sub-Structures: hover dropdown -->
            <div
              v-if="item.id === 'sub-structures'"
              class="nav-item-dropdown-wrapper"
              @mouseenter="showSubMenu = true"
              @mouseleave="showSubMenu = false"
            >
              <button
                class="nav-item nav-item-yellow"
                @click="navigateToSubStructure(subStructures[0])"
              >
                <i :class="item.icon" class="nav-icon"></i>
                <span v-show="isSidebarOpen" class="nav-text">{{ item.text }}</span>
                <i
                  v-show="isSidebarOpen"
                  class="fas nav-chevron"
                  :class="showSubMenu ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </button>
              <div v-show="showSubMenu && isSidebarOpen" class="sub-nav-dropdown">
                <button
                  v-for="child in subStructures"
                  :key="child.id"
                  class="sub-nav-item"
                  @click.stop="navigateToSubStructure(child)"
                >
                  <i class="fas fa-building sub-nav-icon"></i>
                  <span class="sub-nav-name">{{ child.name }}</span>
                  <span v-if="child.abbreviation" class="sub-nav-abbr">({{ child.abbreviation }})</span>
                </button>
              </div>
            </div>
            <!-- Regular nav items -->
            <button
              v-else
              @click="selectItem(item.id)"
              class="nav-item"
              :class="{
                'nav-item-active': selectedItem === item.id,
                'nav-item-yellow': item.id === 'sub-structures',
                'nav-item-red': item.id === 'legal-conformity'
              }"
            >
              <i :class="item.icon" class="nav-icon"></i>
              <span v-show="isSidebarOpen" class="nav-text">
                {{ item.text }}
              </span>
            </button>
          </template>
        </nav>

        <!-- Sidebar Footer (visible only when expanded and email is valid) -->
        <div v-show="isFooterVisible" class="sidebar-footer">
          <div class="contact-card">
            <div class="contact-avatar">
              <img
                :src="structureImageUrl"
                alt="Contact Avatar"
                class="avatar-image"
                @error="handleImageError"
              />
              <span class="contact-name">Contact {{ dashboardName }}</span>
            </div>
            <div class="contact-email">
              {{ structureContact.email }}
            </div>
          </div>
        </div>
      </aside>

      <!-- Main Content Area -->
      <main class="content-area">
        <!-- Mobile Menu Toggle (New Position) -->
        <button
          v-if="isMobile"
          class="mobile-menu-toggle"
          @click="toggleSidebar"
          :aria-label="isSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'"
        >
          <i class="fas fa-bars"></i>
        </button>

        <!-- Parent breadcrumb (shown when this is a child structure) -->
        <div v-if="parentName" class="parent-breadcrumb" @click="navigateToParent">
          <i class="fas fa-arrow-left"></i>
          <span>{{ parentName }}</span>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="loading-overlay">
          <div class="loading-spinner"></div>
          <p class="loading-text">{{ $t('loading') }}</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-container">
            <div class="error-content-box">
                <div class="error-icon-wrapper">
                    <i class="fas fa-exclamation-triangle"></i>
                </div>
                <h3 class="error-main-title">{{ $t('error.title') }}</h3>
                <p class="error-details-text">{{ error }}</p>
                <p class="error-suggestion-text">
                    {{ $t('error.suggestion') }}
                </p>
                <button @click="retryLoading" class="primary-retry-button">
                    <i class="fas fa-sync-alt mr-2"></i> {{ $t('error.retry') }}
                </button>
            </div>
        </div>

        <!-- Content Views -->
        <div v-else>
            <!-- Home View (default) -->
            <section v-if="!selectedItem || selectedItem === 'home'" class="content-section">
              <div class="grid-container">
                <!-- About Section Card -->
                <div class="about-section card-style">
                  <div class="image-container relative">
                    <img
                      :src="structureImageUrl"
                      alt="Structure Image"
                      class="structure-image"
                      @error="handleImageError"
                    />
                    <div class="image-overlay"></div>
                  </div>

                  <div class="p-6">
                    <div class="flex items-center gap-2 flex-wrap">
                      <h2 class="structure-title">{{ structureName }}</h2>
                      <!-- Physical Badge — verified seal with ANTIC logo -->
                      <span
                        v-if="allStructures?.type === 'private' && conformitySummary?.is_physically_compliant"
                        class="physical-badge"
                        :title="$t('physicallyVerified')"
                        @click="selectedItem = 'legal-conformity'"
                      >
                        <svg class="badge-seal" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
                          <defs>
                            <linearGradient id="sealGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                              <stop offset="0%"   stop-color="#007A5E"/>
                              <stop offset="33%"  stop-color="#007A5E"/>
                              <stop offset="33%"  stop-color="#CE1126"/>
                              <stop offset="66%"  stop-color="#CE1126"/>
                              <stop offset="66%"  stop-color="#FCD116"/>
                              <stop offset="100%" stop-color="#FCD116"/>
                            </linearGradient>
                          </defs>
                          <!-- Starburst spikes -->
                          <polygon points="18,1 20.5,6.5 26.5,4 25.5,10.5 32,12 28.5,17 32,22 25.5,23.5 26.5,30 20.5,28.5 18,34 15.5,28.5 9.5,30 10.5,23.5 4,22 7.5,17 4,12 10.5,10.5 9.5,4 15.5,6.5" fill="url(#sealGrad)" />
                          <!-- White inner circle -->
                          <circle cx="18" cy="18" r="8.5" fill="rgba(255,255,255,0.22)"/>
                          <!-- ANTIC logo -->
                          <image href="/favicon.png" x="9" y="9" width="18" height="18" opacity="1"/>
                        </svg>
                      </span>
                    </div>

                    <!-- ── Conformity Checkboxes Row ─────────────────────── -->
                    <div
                      v-if="allStructures?.type === 'private' && legalConformityData.length"
                      class="conformity-icons-row"
                    >
                      <template v-for="item in legalConformityData" :key="item.id">
                        <span
                          class="req-icon-wrap"
                          :title="item.requirement_name"
                          @click="selectedItem = 'legal-conformity'"
                        >
                          <span class="req-icon" :class="item.is_compliant ? 'req-compliant' : 'req-not-compliant'">
                            <i :class="item.is_compliant ? 'fas fa-check' : 'fas fa-times'"></i>
                          </span>
                        </span>
                      </template>
                      <span
                        class="conformity-percent-badge"
                        @click="selectedItem = 'legal-conformity'"
                        :data-tooltip="$t('conformityScore')"
                      >
                        ({{ conformitySummary?.score_percent || 0 }}% {{ $t('compliant') }})
                      </span>
                    </div>

                    <div class="about-content">
                      <h3 class="section-title">{{ $t('about') }} </h3>
                      <hr class="divider" />
                      <p class="description-text">
                        {{ structureDescription }}
                      </p>
                        <a
                          v-if="isValidValue(structureContact.website_url)"
                          :href="structureContact.website_url"
                          class="read-more"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {{ $t('more') }}
                        </a>
                    </div>
                  </div>
                </div>

                <!-- Boss Card -->
                <div class="boss-card card-style">
                  <h3 class="boss-title">{{ bossTitle }}</h3>
                  <div class="boss-content">
                    <img
                      :src="getBossPhotoUrl(structureBoss?.photo)"
                      alt="Boss Photo"
                      class="boss-image"
                      @error="handleBossImageError"
                    />
                    <h4 class="boss-name">{{ bossName }}</h4>
                    <div class="boss-social-links">
                      <div v-for="(contact, key) in filteredSocialContacts" :key="key" class="social-link-item">
                        <a
                          v-if="isValidValue(contact.value)"
                          :href="contact.url(contact.value)"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="social-link"
                          @click.prevent="openExternalLinks(contact.url(contact.value))"
                        >
                          <i :class="contact.iconClass" class="social-icon"></i>
                          <span class="social-value">
                            {{ contact.platform }}
                          </span>
                        </a>
                        <div v-else class="social-link-disabled">
                          <i :class="contact.iconClass" class="social-icon"></i>
                          <span class="social-text" style="color: red;">
                            {{ $t('noContactAvailable', { platform: contact.platform || 'contact' }) }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Contact Section -->
              <div class="contact-section card-style">
                <h3 class="section-title">{{ $t('contactUs') }}</h3>
                <hr class="divider" />

                <div class="contact-grid">
                  <div class="contact-column" v-for="(column, index) in chunkedContactItems" :key="index">
                    <div
                      v-for="item in column"
                      :key="item.id"
                      @click="handleContactClick(item)"
                      :class="{ 'cursor-pointer': getDisplayLink(item) }"
                      class="contact-item-wrapper"
                    >
                      <ContactItem
                        :icon="item.icon"
                        :value="getDisplayValue(item)"
                        :link="getDisplayLink(item)"
                        :type="item.type"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <!-- Services View (replaces Missions) -->
            <section v-if="selectedItem === 'services'" class="content-section">
              <h2 class="section-header">{{ structureName }}</h2>
              <div class="mission-content">
                <h3 class="section-title">{{ $t('services') }}</h3>
                <hr class="divider" />

                <div v-if="structureMissions?.length" class="missions-grid">
                  <div v-for="(mission, index) in structureMissions" :key="mission.id || index" class="mission-card card-style">
                    <div class="mission-icon">
                      <i :class="getMissionIcon(index)"></i>
                    </div>
                    <div class="mission-details">
                      <h4 class="mission-title">{{ mission.mission_title || $t('serviceNumber', { number: index + 1 }) }}</h4>
                      <p class="mission-description">
                        {{ mission.description || $t('noDescriptionAvailable') }}
                      </p>
                    </div>
                  </div>
                </div>

                <div v-else class="no-missions">
                  <div class="coming-soon-container">
                    <i class="fas fa-hourglass-half coming-soon-icon"></i>
                    <h3 class="coming-soon-title">{{ $t('nomissionsTtile') }}</h3>
                    <p class="coming-soon-text">{{ $t('nomissiondescription') }}</p>
                    <p class="coming-soon-note">{{ $t('nomissionnote') }}</p>
                  </div>
                </div>
              </div>
            </section>

            <!-- Legal Conformity View -->
            <section v-if="selectedItem === 'legal-conformity'" class="content-section">
              <h2 class="section-header">{{ structureName }}</h2>
              <div class="legal-conformity-content">
                <!-- Title + Stars inline -->
                <div class="conformity-header-row">
                  <h3 class="section-title">{{ $t('legalConformity') }}</h3>
                  <div v-if="conformitySummary" class="conformity-star-summary">
                    <div class="star-row">
                      <template v-for="n in 5" :key="n">
                        <i class="fas" :class="{
                          'fa-star text-yellow-400': n <= Math.floor(conformitySummary.star_rating),
                          'fa-star-half-alt text-yellow-400': n === Math.ceil(conformitySummary.star_rating) && conformitySummary.star_rating % 1 >= 0.5,
                          'fa-star text-gray-300': n > Math.ceil(conformitySummary.star_rating)
                        }"></i>
                      </template>
                      <span class="star-label">{{ conformitySummary.compliant_count }}/{{ conformitySummary.total_requirements }} {{ $t('requirementsMet') }}</span>
                    </div>
                  </div>
                </div>
                <hr class="divider" />

                <div v-if="conformityLoading" class="flex justify-center py-8">
                  <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
                </div>

                <div v-else-if="legalConformityData.length" class="conformity-grid-wrap">
                  <div class="conformity-reqs-grid">
                    <div
                      v-for="item in legalConformityData"
                      :key="item.id"
                      class="conformity-card"
                      :class="item.is_compliant ? 'card-compliant' : 'card-not-compliant'"
                    >
                      <span class="card-checkbox" :class="item.is_compliant ? 'cb-compliant' : 'cb-not-compliant'">
                        <i :class="item.is_compliant ? 'fas fa-check' : 'fas fa-times'"></i>
                      </span>
                      <div class="card-body">
                        <span class="card-name">{{ item.requirement_name }}</span>
                        <p v-if="item.requirement_description" class="card-desc">{{ item.requirement_description }}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-else class="no-missions">
                  <div class="coming-soon-container">
                    <i class="fas fa-shield-alt coming-soon-icon"></i>
                    <h3 class="coming-soon-title">{{ $t('noConformityData') }}</h3>
                    <p class="coming-soon-text">{{ $t('noConformityDesc') }}</p>
                  </div>
                </div>
              </div>
            </section>


            <!-- Boss View -->
            <section v-if="selectedItem === 'boss'" class="content-section">
              <div class="boss-detail-card card-style">
                <div class="boss-header">
                  <h2 class="section-header">{{ bossTitle }}</h2>
                </div>
                <div class="boss-detail-content">
                  <div class="boss-profile">
                    <img
                      :src="getBossPhotoUrl(structureBoss?.photo)"
                      alt="Boss Photo"
                      class="boss-detail-image"
                      @error="handleBossImageError"
                    />
                    <h3 class="boss-detail-name">{{ bossName }}</h3>
                    <p class="boss-position">{{ structureBoss?.title }}</p>
                  </div>

                  <div class="boss-bio-section" v-if="bossBio">
                    <h4 class="bio-title">{{ $t('biography') }}</h4>
                    <p class="boss-bio">{{ bossBio }}</p>
                  </div>

                  <div class="boss-contact-section">
                    <h4 class="contact-title">{{ $t('bossContactSection') }}</h4>
                    <div class="boss-social-grid">
                      <div v-for="(contact, key) in filteredSocialContacts" :key="key" class="social-item">
                        <a
                          v-if="isValidValue(contact.value)"
                          :href="contact.url(contact.value)"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="social-link-detailed"
                          @click.prevent="openExternalLinks(contact.url(contact.value))"
                        >
                          <i :class="contact.iconClass" class="social-icon-detailed"></i>
                          <div class="social-info">
                            <span class="social-platform" style="align-items: left; text-align: left;">{{ contact.platform }}: </span>
                            <span class="social-value-detailed" style="text-overflow: hidden;"> {{ contact.value }}</span>
                          </div>
                        </a>
                        <div v-else class="social-item-disabled">
                          <i :class="contact.iconClass" class="social-icon-detailed"></i>
                          <div class="social-info">
                            <span class="social-platform"> {{ contact.platform }} : </span>
                            <span class="social-text-detailed" style="color: red;">  {{ $t('noContactAvailable') }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <!-- Contact View -->
            <section v-if="selectedItem === 'contact'" class="content-section">
              <h2 class="section-header">{{ $t('contactUs') }}</h2>
              <div class="contact-detail-grid">
                 <div
                    v-for="item in contactItems"
                    :key="item.id"
                    @click="handleContactClick(item)"
                    :class="{ 'cursor-pointer': getDisplayLink(item) }"
                  >
                  <ContactItem
                    :icon="item.icon"
                    :value="getDisplayValue(item)"
                    :link="getDisplayLink(item)"
                    :type="item.type"
                    detailed
                  />
                </div>
              </div>
            </section>

            <!-- Regional View -->
            <section v-if="selectedItem === 'regional' && structureHasRegionals" class="content-section">
              <h2 class="section-header">{{ $t('regionals') }}</h2>
              <div v-if="structureRegional.length">
                <RegionalRepresentative
                  :cards="structureRegional"
                  class="regional-component"
                />
              </div>
              <div v-else class="no-regionals">
                <div class="coming-soon-container">
                  <i class="fas fa-map-marked-alt coming-soon-icon"></i>
                  <h3 class="coming-soon-title">{{ $t('noRegionalsTitle') }}</h3>
                  <p class="coming-soon-text">{{ $t('noRegionalsDescription') }}</p>
                </div>
              </div>
            </section>

            <!-- Personnel View -->
            <section v-if="selectedItem === 'key-personnels'" class="content-section">
              <h2 class="section-header">{{ $t('otherspersonnels') }}</h2>
              <OtherPersonels
                :personels="StatePersonelsData"
                class="personnel-component"
              />
            </section>

            <!-- Embassies View -->
            <section v-if="selectedItem === 'embassies'" class="content-section">
              <!-- <h2 class="section-header">{{ $t('embassiesAndConsulates') }}</h2> -->
              <EmbassiesList class="embassies-component" />
            </section>
        </div>
      </main>
    </div>
  </div>
 
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
// import axios from 'axios';
import { inject } from 'vue';
import RegionalRepresentative from "@/components/common/RegionalRepresentative.vue";
import OtherPersonels from "@/components/common/OtherPersonels.vue";
import EmbassiesList from '@/components/common/EmbassiesList.vue';
import ContactItem from "@/components/common/ContactItem.vue";
import { useI18n } from 'vue-i18n';
import { useSectorStore } from '@/stores/sector';
import { useStructureStore } from '@/stores/structure';

const BaseURL = process.env.VUE_APP_API_BASE_URL;

export default {
  name: "StructureDisplayPage",
  components: {
    RegionalRepresentative,
    OtherPersonels,
    ContactItem,
    EmbassiesList
  },
  props: {
    lang: String,
    name: String
  },
  setup() {
    // Composables
    const apiClient = inject('api');
    const { t, locale } = useI18n();
    const route = useRoute();
    const router = useRouter();
    const sectorStore = useSectorStore();
    const structureStore = useStructureStore();
    sectorStore._ensureCacheLoaded();
    structureStore._ensureCacheLoaded();

    // Refs
    const isSidebarOpen = ref(false);
    const selectedItem = ref('home');
    const isMobile = ref(false);
    const isLoading = ref(false);
    const error = ref(null);
    const showSubMenu = ref(false);

    // Helper functions
    const isValidValue = (val) => {
      if (val === null || val === undefined) return false;
      const s = String(val).toLowerCase().trim();
      return s !== '' && s !== 'nan' && s !== 'none';
    };
    
    const isValidEmail = (email) => {
        if (!isValidValue(email)) return false;
        // Basic regex for email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(String(email));
    };

    const getFullImageUrl = (path) => {
      if (!isValidValue(path)) return null;
      if (path.startsWith('http')) return path;
      return `${BaseURL}${path.startsWith('/') ? '' : '/'}${path}`;
    };

    const getBossPhotoUrl = (photo) => {
      return isValidValue(photo) ? getFullImageUrl(photo) : '/assets/images/avatar.png';
    };

    // Store values
    const strucId = computed(() => structureStore.currentStructureId || sectorStore.currentSectorId);
    const sectorName = computed(() => route.params.name ? decodeURIComponent(route.params.name) : '');

    // Computed properties
    const allStructures = computed(() => structureStore.allStructures);
    const structureImageUrl = computed(() => getFullImageUrl(structureStore.structureImageUrl));
    const structureContact = computed(() => structureStore.structureContact || {});
    const structureBoss = computed(() => structureStore.structureBoss);
    const structureBossContact = computed(() => structureStore.structureBossContact || {});
    const structureRegional = computed(() => structureStore.structureRegional);
    const StatePersonelsData = computed(() => structureStore.StatePersonelsData);
    const structureMissions = computed(() => structureStore.structureMissions);

    // Legal conformity state
    const legalConformityData = ref([]);
    const conformitySummary = ref(null);
    const conformityLoading = ref(false);
    const availableCategories = ref([]);
    const selectedCategoryId = ref('');
    const assigningCategory = ref(false);
    const assignError = ref('');

    const groupedConformity = computed(() => {
      const groups = {};
      for (const item of legalConformityData.value) {
        const cat = item.category_name || 'General';
        if (!groups[cat]) groups[cat] = [];
        groups[cat].push(item);
      }
      return groups;
    });

    const structureHasRegionals = computed(() => {
      return structureStore.has_regionals;
    });

    const subStructures = computed(() => allStructures.value?.sub_structures || []);

    const parentName = computed(() => allStructures.value?.parent_name || null);

    const navigateToParent = () => {
      const parentId = allStructures.value?.parent;
      if (!parentId) return;
      structureStore.clearCurrentStructure();
      structureStore.currentStructureId = parentId;
      structureStore._saveToCache();
      router.push({
        name: 'structure-details',
        params: { lang: route.params.lang, name: encodeURIComponent(parentName.value) }
      });
    };

    const navigateToSubStructure = (child) => {
      if (!child) return;
      structureStore.clearCurrentStructure();
      structureStore.currentStructureId = child.id;
      structureStore.currentStructureName = child.name;
      structureStore._saveToCache();
      showSubMenu.value = false;
      router.push({
        name: 'structure-details',
        params: { lang: route.params.lang, name: encodeURIComponent(child.name) }
      });
    };

    const isFooterVisible = computed(() => {
      return isSidebarOpen.value && isValidEmail(structureContact.value?.email);
    });

    const dashboardName = computed(() => isValidValue(structureStore.dashboardName) ? structureStore.dashboardName : allStructures.value.name);
    const structureName = computed(() => isValidValue(allStructures.value?.name) ? allStructures.value.name : t('noDataAvailable'));
    const structureDescription = computed(() => isValidValue(allStructures.value?.description) ? allStructures.value.description : t('noDescriptionAvailable'));
    const bossTitle = computed(() => isValidValue(structureBoss.value?.title) ? structureBoss.value.title : t('head'));
    const bossName = computed(() => isValidValue(structureBoss.value?.name) ? structureBoss.value.name : t('noInformationAvailable'));
    const bossBio = computed(() => isValidValue(structureBoss.value?.bio) ? structureBoss.value.bio : "");
    const structureEmail = computed(() => isValidValue(structureContact.value?.structureEmail) ? structureContact.value.structureEmail : "");

    const contactItems = [
        { id: 1, icon: 'fas fa-globe', field: 'website_url', type: 'url', link: val => val },
        { id: 2, icon: 'far fa-envelope', field: 'email', type: 'email', link: val => val ? `mailto:${val}`: null },
        { id: 3, icon: 'fas fa-phone', field: 'phone', type: 'tel', link: val => val ? `tel:${val}`: null },
        { id: 4, icon: 'fab fa-facebook-f', field: 'facebook_url', type: 'url', link: val => val },
        { id: 5, icon: 'fab fa-twitter', field: 'twitter_url', type: 'url', link: val => val },
        { id: 6, icon: 'fab fa-youtube', field: 'youtube_url', type: 'url', link: val => val },
        { id: 7, icon: 'fab fa-instagram', field: 'instagram_url', type: 'url', link: val => val },
        { id: 8, icon: 'fab fa-linkedin', field: 'linkedin_url', type: 'url', link: val => val },
        { id: 9, icon: 'fab fa-tiktok', field: 'tiktok_url', type: 'url', link: val => val },
        { id: 10, icon: 'fas fa-map-marker-alt', field: 'location', type: 'location' },
    ];

    const chunkedContactItems = computed(() => {
      const chunkSize = Math.ceil(contactItems.length / 3);
      return [
        contactItems.slice(0, chunkSize),
        contactItems.slice(chunkSize, chunkSize * 2),
        contactItems.slice(chunkSize * 2)
      ];
    });

    const isMapClickable = computed(() => {
        const name = structureName.value;
        const location = structureContact.value?.location;
        return (isValidValue(name) && name !== t('noDataAvailable')) || isValidValue(location);
    });

    const getDisplayValue = (item) => {
        const rawValue = structureContact.value[item.field];
        if (item.field === 'location') {
            return isValidValue(rawValue) ? rawValue : t('noLocationAvailable');
        }
        return isValidValue(rawValue) ? rawValue : null;
    };

    const getDisplayLink = (item) => {
        const rawValue = structureContact.value[item.field];
        if (item.type === 'location') {
            return isMapClickable.value;
        }
        if (isValidValue(rawValue) && typeof item.link === 'function') {
            return item.link(rawValue);
        }
        return null;
    };

    const socialContacts = {
      facebook_url: { platform: 'Facebook', iconClass: 'fab fa-facebook-f', url: val => val },
      twitter_url: { platform: 'Twitter', iconClass: 'fab fa-twitter', url: val => val },
      email: { platform: 'Email', iconClass: 'far fa-envelope', url: val => `mailto:${val}` },
      instagram_url: { platform: 'Instagram', iconClass: 'fab fa-instagram', url: val => val },
      linkedin_url: { platform: 'LinkedIn', iconClass: 'fab fa-linkedin', url: val => val },
      tiktok_url: { platform: 'TikTok', iconClass: 'fab fa-tiktok', url: val => val },
      youtube_url: { platform: 'YouTube', iconClass: 'fab fa-youtube', url: val => val },
    };

    const filteredSocialContacts = computed(() => {
      return Object.keys(socialContacts)
        .map(key => ({ ...socialContacts[key], key, value: structureBossContact.value[key] }));
    });

    const baseMenuItems = computed(() => {
    const isPrivate = allStructures.value?.type === 'private';
    const items = [
      { id: 'home', icon: 'fas fa-home', text: t('home') },
      isPrivate
        ? { id: 'services', icon: 'fas fa-concierge-bell', text: t('services') }
        : { id: 'services', icon: 'fas fa-bullseye', text: t('missions') },
      { id: 'boss', icon: 'fas fa-user-tie', text: bossTitle.value },
      { id: 'contact', icon: 'fas fa-address-book', text: t('contactUs') },
      { id: 'key-personnels', icon: 'fas fa-users', text: t('otherspersonnels') }
    ];

    // Legal conformity only for private sector
    if (isPrivate) {
      items.splice(2, 0, { id: 'legal-conformity', icon: 'fas fa-shield-alt', text: t('legalConformity') });
    }

    if (structureHasRegionals.value) {
      items.splice(isPrivate ? 5 : 4, 0, { id: 'regional', icon: 'fas fa-map-marked-alt', text: t('regionals') });
    }
      
      if (dashboardName.value === 'MINREX') {
        items.splice(4, 0, { id: 'embassies', icon: 'fas fa-flag', text: t('embassies') });
      }

      if (subStructures.value.length > 0) {
        items.push({ id: 'sub-structures', icon: 'fas fa-sitemap', text: t('subStructures') });
      }
      
      return items;
    });

    const filteredMenuItems = computed(() => {
      return baseMenuItems.value;
    });

    // Methods
    const checkInitialData = () => {
      return (
        allStructures.value &&
        Object.keys(allStructures.value).length > 0 &&
        structureBoss.value &&
        Object.keys(structureBoss.value).length > 0
      );
    };

    const openExternalLinks = (url) => {
      if (isValidValue(url)) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    };

    const handleMapClick = async () => {
        const name = structureName.value;
        const location = structureContact.value?.location;
        let destinationQuery = '';

        // Prioritize the structure's name for the destination search query
        if (isValidValue(name) && name !== t('noDataAvailable')) {
            destinationQuery = name;
        } else if (isValidValue(location)) {
            destinationQuery = location; // Fallback to location text
        }

        if (!destinationQuery) {
            return;
        }

        // Check for Geolocation API support
        if ('geolocation' in navigator) {
            // Attempt to get user's current position
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    // SUCCESS: User allowed location access
                    const { latitude, longitude } = position.coords;
                    // Construct a directions URL with origin (user) and destination (structure)
                    const directionsUrl = `https://www.google.com/maps/dir/?api=1&origin=${latitude},${longitude}&destination=${encodeURIComponent(destinationQuery)}`;
                    openExternalLinks(directionsUrl);
                },
                () => {
                    // Fallback to a simple search URL, as user denied live tracking
                    const searchUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(destinationQuery)}`;
                    openExternalLinks(searchUrl);
                },
                {
                    // Geolocation options
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 0
                }
            );
        } else {
            const searchUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(destinationQuery)}`;
            openExternalLinks(searchUrl);
        }
    };


    const handleContactClick = (item) => {
      if (item.type === 'location') {
        handleMapClick();
      } else {
        const link = getDisplayLink(item);
        if (link && typeof link === 'string') {
          openExternalLinks(link);
        }
      }
    };

    const getMissionIcon = (index) => {
      const icons = ['fas fa-bullseye', 'fas fa-rocket', 'fas fa-lightbulb', 'fas fa-handshake', 'fas fa-chart-line', 'fas fa-globe', 'fas fa-heart', 'fas fa-graduation-cap', 'fas fa-leaf', 'fas fa-users'];
      return icons[index % icons.length];
    };

    const toggleSidebar = () => { isSidebarOpen.value = !isSidebarOpen.value; };

    const selectItem = (item) => {
      if (selectedItem.value !== item) {
        selectedItem.value = item;
        router.push({ query: { ...route.query, section: item } });
      }
      if (isMobile.value) { isSidebarOpen.value = false; }
    };

    const checkScreenSize = () => {
      isMobile.value = window.innerWidth <= 768;
      isSidebarOpen.value = !isMobile.value;
    };

    const handleImageError = () => { structureStore.structureImageUrl = '/assets/images/default-structure.jpg'; };
    const handleBossImageError = (event) => { event.target.src = '/assets/images/avatar.png'; };
    const retryLoading = () => { error.value = null; loadStructureData(); };

    // Data loading
    const loadStructureData = async () => {
      if (!strucId.value) {
        error.value = t('error.invalidStructure');
        return;
      }
      if (isLoading.value) return; // prevent concurrent double-load
      
      isLoading.value = true;
      error.value = null;
      
      try {
        let structureResponse;
        try {
          structureResponse = await apiClient.get(`/structures/${strucId.value}`);
        } catch (publicError) {
          structureResponse = await apiClient.get(`/private-structure/${strucId.value}`);
        }

        if (structureResponse?.data) {
          structureStore.setStructureData(structureResponse.data);
          
          await Promise.all([
            fetchStructureContact(),
            fetchStatePersonnels(),
            fetchStructureRegionals(),
            fetchStructureBoss()
          ]);
          
          if (structureBoss.value?.id) {
            await fetchStructureBossContact();
          }
          
          await fetchStructureMissions();
          await fetchLegalConformity();
          await fetchCategories();
        } else {
          throw new Error('No structure data received from either endpoint');
        }
      } catch (err) {
          error.value = t('error.fetchingData');
      } finally {
          isLoading.value = false;
      }
    };
    
    const fetchStructureContact = async () => {
    try { 
        const response = await apiClient.get(
            `/structure_retrieve_contacts/?structureId=${strucId.value}`
        ); 
        structureStore.setStructureContact(response.data?.[0] || {}); 
    } catch (err) { 
        
        structureStore.setStructureContact({});
    }
        };
    
    const fetchStructureBoss = async () => {
      try { 
        const response = await apiClient.get(
          `/structure_retrieve_boss/?structureId=${strucId.value}`
          
        ); 
        const bossData = response.data?.[0]; 
        if (bossData) { 
          structureStore.setStructureBoss(bossData); 
        } 
      } catch (err) { 
        structureStore.setStructureBoss(null);
      }
    };
    
    const fetchStructureBossContact = async () => {
      try { 
        const response = await apiClient.get(
          `/boss_retrieve_contacts/?structureId=${structureBoss.value.boss_structure}`
        ); 
        structureStore.setStructureBossContact(response.data?.[0] || {}); 
      } catch (err) { 
        structureStore.setStructureBossContact({});
      }
    };
    
    const fetchStructureRegionals = async () => {
      try { 
        const response = await apiClient.get(
          `/structure_retrieve_regionals/?structureId=${strucId.value}`
        ); 
        const regionals = (response.data || []).map(item => ({ 
          ...item, 
          photo: item.photo ? getFullImageUrl(item.photo) : '/assets/images/default-regional.png' 
        })); 
        structureStore.setStructureRegionals(regionals); 
      } catch (err) { 
        structureStore.setStructureRegionals([]);
      }
    };
    
    const fetchStatePersonnels = async () => {
      try { 
        const response = await apiClient.get(
          `/state_personnels/?structureId=${strucId.value}`
        ); 
        const personnels = (response.data || []).map(item => ({ 
          ...item, 
          photo: item.photo ? getFullImageUrl(item.photo) : '/assets/images/avatar.png' 
        })); 
        structureStore.setStatePersonnels(personnels); 
      } catch (err) { 
        structureStore.setStatePersonnels([]);
      }
    };
    
    const fetchStructureMissions = async () => {
      try { 
        const response = await apiClient.get(
          `/structure_retrieve_missions/?structureId=${strucId.value}`
        ); 
        const missions = Array.isArray(response.data) ? response.data : (response.data ? [response.data] : []); 
        structureStore.setStructureMissions(missions); 
      } catch (err) { 
        structureStore.setStructureMissions([]);
      }
    };

    const fetchLegalConformity = async () => {
      if (!strucId.value || allStructures.value?.type !== 'private') return;
      conformityLoading.value = true;
      try {
        const [conformityRes, summaryRes] = await Promise.allSettled([
          apiClient.get(`/structure-legal-conformity/?structure_id=${strucId.value}`),
          apiClient.get(`/structure-legal-conformity/summary/?structure_id=${strucId.value}`),
        ]);
        legalConformityData.value = conformityRes.status === 'fulfilled'
          ? (conformityRes.value.data?.results || conformityRes.value.data || []) : [];
        conformitySummary.value = summaryRes.status === 'fulfilled' ? summaryRes.value.data : null;
      } catch {
        legalConformityData.value = [];
        conformitySummary.value = null;
      } finally {
        conformityLoading.value = false;
      }
    };

    const fetchCategories = async () => {
      try {
        const res = await apiClient.get('/legal-conformity-categories/');
        availableCategories.value = res.data?.results || res.data || [];
      } catch { /* ignored */ }
    };

    const assignCategory = async () => {
      if (!selectedCategoryId.value) return;
      assigningCategory.value = true;
      assignError.value = '';
      try {
        await apiClient.post('/structure-legal-conformity/assign-category/', {
          structure_id: strucId.value,
          category_id: selectedCategoryId.value,
        });
        selectedCategoryId.value = '';
        await fetchLegalConformity();
      } catch (e) {
        assignError.value = e?.response?.data?.error || 'Assignment failed.';
      } finally {
        assigningCategory.value = false;
      }
    };

    const toggleCompliance = async (item) => {
      try {
        const res = await apiClient.patch(
          `/structure-legal-conformity/${item.id}/toggle/`,
          { is_compliant: !item.is_compliant }
        );
        const idx = legalConformityData.value.findIndex(i => i.id === item.id);
        if (idx !== -1) legalConformityData.value[idx] = res.data;
        await fetchLegalConformity(); // refresh summary/stars
      } catch { /* ignored */ }
    };

    // Lifecycle hooks
    onMounted(() => {
      checkScreenSize();
      window.addEventListener('resize', checkScreenSize);
      // Always reload — stale store data from previous page visits
      // causes badges and conformity to not appear until forced refresh.
      loadStructureData();
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', checkScreenSize);
    });

    // Watchers
    watch(strucId, (newId, oldId) => {
      if (newId && newId !== oldId) {
        // Do NOT call $reset() here — it sets currentStructureId=null,
        // which re-triggers this watcher and causes a double load.
        loadStructureData();
      } else if (newId && !checkInitialData()) {
        loadStructureData();
      }
    });

    watch(() => route.query.section, (newSection) => {
      const sectionToSelect = (newSection && baseMenuItems.value.some(item => item.id === newSection)) 
        ? newSection 
        : 'home';
      if (selectedItem.value !== sectionToSelect) {
        selectItem(sectionToSelect);
      }
    }, { immediate: true });

    watch(
      () => locale.value,
      async () => {
        // Re-fetch all data in the new language.
        // The interceptor sends the correct Accept-Language automatically.
        // Store setters overwrite old values in place — no reset needed.
        if (!strucId.value) return;
        isLoading.value = true;
        error.value = null;
        try {
          await loadStructureData();
        } finally {
          isLoading.value = false;
        }
      }
    );

    return {
      isSidebarOpen,
      selectedItem,
      isMobile,
      isLoading,
      error,
      allStructures,
      structureImageUrl,
      dashboardName,
      structureContact,
      structureBoss,
      structureBossContact,
      structureRegional,
      StatePersonelsData,
      structureMissions,
      legalConformityData,
      conformitySummary,
      conformityLoading,
      availableCategories,
      selectedCategoryId,
      assigningCategory,
      assignError,
      assignCategory,
      toggleCompliance,
      groupedConformity,
      filteredMenuItems,
      filteredSocialContacts,
      contactItems,
      structureName,
      structureDescription,
      bossTitle,
      bossName,
      bossBio,
      structureEmail,
      structureHasRegionals,
      subStructures,
      parentName,
      navigateToParent,
      navigateToSubStructure,
      showSubMenu,
      isFooterVisible,
      toggleSidebar,
      selectItem,
      handleImageError,
      handleBossImageError,
      getBossPhotoUrl,
      getMissionIcon,
      openExternalLinks,
      retryLoading,
      sectorName,
      isValidValue,
      getDisplayValue,
      getDisplayLink,
      handleContactClick,
      chunkedContactItems
    };
  }
};
</script>

<style scoped>
/* Base Styles */
.page-container {
  @apply min-h-screen flex flex-col;
}

.main-layout {
  @apply flex flex-col md:flex-row flex-1;
}

/* Mobile Menu Toggle */
.mobile-menu-toggle {
  @apply self-start p-2 bg-[#144A78] text-white rounded-md shadow-lg mb-4 hover:bg-blue-800 transition-colors;
  @apply md:hidden;
}

/* Sidebar Styles */
.sidebar {
  @apply bg-[#144A78] text-white flex flex-col transition-all duration-300 ease-in-out;
  @apply w-full md:w-64 fixed md:relative h-full md:h-auto z-40;
  @apply transform -translate-x-full md:translate-x-0;
  margin: 0;
}

.sidebar-mobile-open {
  @apply translate-x-0;
}

.sidebar-mobile-hidden {
  @apply -translate-x-full;
}

.sidebar-collapsed {
  @apply md:w-20;
}

.sidebar-header {
  @apply flex items-center justify-between mb-8 p-4;
}

.sidebar-title {
  @apply text-xl font-bold truncate;
}

.sidebar-toggle {
  @apply p-2 rounded-md hover:bg-blue-700 transition-colors;
  @apply md:block hidden;
}

.sidebar-nav {
  @apply space-y-2 px-4 flex-1;
}

.nav-item {
  @apply flex items-center py-2.5 px-3 rounded-lg transition-all duration-200 w-full text-left;
  @apply hover:bg-blue-700;
}

.nav-item-active {
  @apply bg-blue-700;
}

.nav-icon {
  @apply min-w-[24px] text-center text-lg;
}

.nav-text {
  @apply ml-3 font-bold whitespace-normal truncate;
}

.sidebar-footer {
  @apply mt-auto p-4;
}

.contact-card {
  @apply bg-white rounded-lg p-3 text-blue-900;
}

.contact-avatar {
  @apply flex items-center mb-2;
}

.avatar-image {
  @apply w-8 h-8 object-cover rounded-full border-2 border-blue-200;
}

.contact-name {
  @apply ml-2 font-medium;
}

.contact-email {
  @apply text-sm break-all;
}

/* Main Content Styles */
.content-area {
  @apply flex flex-col flex-1 p-4 md:p-6 bg-white;
  position: relative;
}

.content-section {
  @apply mb-8;
}

.card-style {
  @apply bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-300;
}

.card-style:hover {
  @apply shadow-xl transform -translate-y-1;
}

.grid-container {
  @apply grid grid-cols-1 lg:grid-cols-3 gap-6;
}

.about-section {
  @apply lg:col-span-2;
}

.image-container {
  @apply relative overflow-hidden;
}

.structure-image {
  @apply w-full h-48 md:h-64 object-cover;
}

.image-overlay {
  @apply absolute inset-0 bg-gradient-to-t from-black/50 to-transparent;
}

.structure-title {
  @apply text-2xl md:text-3xl font-bold mb-4 text-blue-900 text-left;
}

.about-content {
  @apply text-left;
}

.section-title {
  @apply text-lg md:text-xl font-bold my-2 text-blue-900;
}

.divider {
  @apply border-t border-blue-200 my-4;
}

.description-text {
  @apply text-gray-700 mb-4 text-base md:text-lg leading-relaxed;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.read-more {
  @apply text-blue-600 hover:underline font-semibold;
}

/* Boss Card Styles */
.boss-card {
  @apply bg-[#144A78] text-white p-6;
}

.boss-title {
  @apply text-lg font-bold mb-4 border-b border-blue-500 pb-2;
}

.boss-content {
  @apply bg-white w-full flex flex-col items-center p-4 rounded-lg;
}

.boss-image {
  @apply w-24 h-24 rounded-full mb-3 object-cover border-4 border-white shadow-md;
}

.boss-name {
  @apply text-lg font-semibold mb-2 text-black;
}

.boss-social-links {
  @apply w-full grid grid-cols-2 gap-3 mt-4;
}

.social-link-item {
  @apply w-full;
}

.social-link {
  @apply flex items-center p-2 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors;
}

.social-link-disabled {
  @apply flex items-center p-2 rounded-lg bg-gray-100 text-gray-500;
}

.social-icon {
  @apply w-5 h-5 flex-shrink-0 text-blue-600;
}

.social-value {
  @apply ml-2 text-sm font-medium text-gray-700;
}

.social-text {
  @apply ml-2 text-sm text-gray-500;
}

/* Contact Section */
.contact-section {
  @apply mt-8 p-6;
}

.contact-grid {
  @apply grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 mt-4;
}

.contact-column {
  @apply space-y-2;
}

.contact-item-wrapper {
  @apply rounded-lg transition-colors duration-200;
}
.contact-item-wrapper:has(a) {
  @apply hover:bg-gray-100;
}

/* Detail Views */
.boss-detail-card {
  @apply bg-white;
}

.boss-header {
  @apply bg-[#144A78] text-white p-6;
}

.boss-detail-content {
  @apply p-6;
}

.boss-profile {
  @apply flex flex-col items-center text-center mb-8;
}

.boss-detail-image {
  @apply w-32 h-32 rounded-full mb-4 object-cover border-4 border-white shadow-lg -mt-20;
}

.boss-detail-name {
  @apply text-2xl font-bold mb-1 text-gray-800;
}

.boss-position {
  @apply text-lg text-blue-600 font-medium mb-4;
}

.boss-bio-section {
  @apply mb-8;
}

.bio-title {
  @apply text-xl font-bold mb-4 text-gray-800 border-b pb-2;
}

.boss-bio {
  @apply text-gray-700 leading-relaxed;
}

.boss-contact-section {
  @apply mt-8;
}

.contact-title {
  @apply text-xl font-bold mb-4 text-gray-800 border-b pb-2;
}

.boss-social-grid {
  @apply grid grid-cols-1 md:grid-cols-2 gap-4;
}

.social-item {
  @apply w-full;
}

.social-link-detailed {
  @apply flex items-center p-4 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors;
}

.social-item-disabled {
  @apply flex items-center p-4 rounded-lg bg-gray-100 text-gray-500;
}

.social-icon-detailed {
  @apply w-6 h-6 flex-shrink-0 text-blue-600;
}

.social-info {
  @apply ml-4 truncate;
  display: flex;
  flex-direction: column;
  align-items: flex-start; /* This ensures left alignment */
}

.social-platform {
  @apply text-sm font-medium text-gray-600;
  text-align: left; /* Explicit left alignment */
  width: 100%; /* Takes full width of parent */
}

.social-value-detailed {
  @apply text-sm text-gray-700 mt-1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  width: 100%; /* Takes full width of parent */
  text-align: left; /* Ensures text alignment */
}

.social-text-detailed {
  @apply text-sm text-gray-500 mt-1;
}

.contact-detail-grid {
  @apply grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6;
}

.section-header {
  @apply text-2xl md:text-3xl font-bold mb-6 text-gray-800;
}

/* Missions Grid */
.missions-grid {
  @apply grid gap-6 mt-6;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
}

.mission-card {
  @apply flex items-start p-4;
}

.mission-icon {
  @apply mr-4 p-3 bg-blue-50 rounded-full text-blue-600;
}

.mission-icon i {
  @apply text-xl;
}

.mission-details {
  @apply flex-1;
}

.mission-title {
  @apply text-lg font-semibold text-gray-800 mb-1;
}

.mission-description {
  @apply text-gray-600 text-sm leading-relaxed;
}

.no-missions {
  @apply p-8 text-center;
}

.coming-soon-container {
  @apply max-w-2xl mx-auto bg-blue-50 bg-opacity-50 p-8 rounded-xl border border-blue-100;
}

.coming-soon-icon {
  @apply text-4xl text-blue-400 mb-4;
}

.coming-soon-title {
  @apply text-xl font-semibold text-blue-800 mb-3;
}

.coming-soon-text {
  @apply text-gray-700 mb-4 leading-relaxed;
}

.coming-soon-note {
  @apply text-sm text-gray-500 italic;
}

/* Loading and Error States */
.loading-overlay {
  @apply fixed inset-0 bg-white bg-opacity-80 flex flex-col items-center justify-center z-50;
}

.loading-spinner {
  @apply animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-800;
}

.loading-text {
  @apply mt-4 text-gray-600;
}

.error-container {
    @apply flex items-center justify-center h-full w-full p-4;
}

.error-content-box {
    @apply bg-white p-8 rounded-lg shadow-xl text-center max-w-lg mx-auto border border-red-200;
}

.error-icon-wrapper {
    @apply text-red-500 text-5xl mb-4;
}

.error-main-title {
    @apply text-2xl font-bold text-gray-800 mb-2;
}

.error-details-text {
    @apply text-gray-600 mb-4;
}

.error-suggestion-text {
    @apply text-sm text-gray-500 mb-6;
}

.primary-retry-button {
    @apply bg-blue-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-blue-700 transition-transform transform hover:scale-105;
}

/* Mobile Specific Styles */
@media (max-width: 768px) {
  .sidebar {
    @apply w-full;
  }
  .sidebar-collapsed {
    @apply w-full;
  }
  .nav-text {
    @apply ml-2;
  }
  .grid-container {
    @apply grid-cols-1;
  }
  .contact-grid {
    @apply grid-cols-1;
  }
  .sidebar-footer {
    @apply hidden;
  }
  .boss-card,
  .contact-section {
    @apply mt-6;
  }
  .mission-card {
    @apply flex-col items-center text-center;
  }
  .mission-icon {
    @apply mr-0 mb-3;
  }
  .boss-detail-image {
    @apply w-24 h-24 -mt-16;
  }
  .boss-social-grid,
  .contact-detail-grid {
    @apply grid-cols-1;
  }
}
.no-regionals {
  @apply p-8 text-center;
}

.coming-soon-container {
  @apply max-w-2xl mx-auto bg-blue-50 bg-opacity-50 p-8 rounded-xl border border-blue-100;
}

.coming-soon-icon {
  @apply text-4xl text-blue-400 mb-4;
}

.coming-soon-title {
  @apply text-xl font-semibold text-blue-800 mb-3;
}

.coming-soon-text {
  @apply text-gray-700 mb-4 leading-relaxed;
}

/* Utility Classes */
.truncate {
  @apply overflow-hidden text-ellipsis whitespace-nowrap;
}

.break-words {
  word-break: break-word;
}

.cursor-pointer {
  cursor: pointer;
}

/* ── Physical Verified Seal badge beside structure name ──────────────────── */
.physical-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  cursor: pointer;
  vertical-align: middle;
  position: relative;
  top: -2px;
  transition: transform 0.18s, filter 0.18s;
}

.physical-badge:hover {
  transform: scale(1.15);
  filter: drop-shadow(0 3px 8px rgba(0,122,94,0.5));
}

.badge-seal {
  width: 34px;
  height: 34px;
}

/* ── Conformity Icons Row (Home section) ──────────────────────────────────── */
.conformity-icons-row {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  margin: 5px 0 12px 0;
}

.req-icon-wrap {
  cursor: pointer;
  position: relative;
}

.req-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  transition: transform 0.15s, box-shadow 0.15s;
  flex-shrink: 0;
}

.req-icon-wrap:hover .req-icon {
  transform: scale(1.25);
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}

.req-compliant {
  background: #16a34a;
  color: #fff;
}

.req-not-compliant {
  background: #dc2626;
  color: #fff;
}

/* ── Percent badge — plain text in brackets ───────────────────────────────── */
.conformity-percent-badge {
  font-size: 12px;
  font-weight: 600;
  color: #4a5568;
  cursor: pointer;
  transition: color 0.15s;
  margin-left: 2px;
}

.conformity-percent-badge:hover {
  color: #007A5E;
}

/* ── Legal Conformity Section ─────────────────────────────────────────────── */
.legal-conformity-content {
  padding: 1rem 1.5rem;
}

.conformity-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.conformity-star-summary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.star-row {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 16px;
}

.star-label {
  margin-left: 8px;
  font-size: 13px;
  color: #4a5568;
}

/* ── Conformity requirements grid — 3 cards per row ─────────────────────── */
.conformity-grid-wrap {
  margin-top: 1rem;
}

.conformity-reqs-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
}

@media (max-width: 900px) {
  .conformity-reqs-grid { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 560px) {
  .conformity-reqs-grid { grid-template-columns: 1fr; }
}

.conformity-card {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.7rem 0.85rem;
  border-radius: 8px;
  border: 1px solid;
  transition: box-shadow 0.15s, transform 0.15s;
}

.conformity-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}

.card-compliant {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.card-not-compliant {
  background: #fff5f5;
  border-color: #fecaca;
}

/* Square checkbox icon */
.card-checkbox {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 5px;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 1px;
}

.cb-compliant {
  background: #16a34a;
  color: #fff;
}

.cb-not-compliant {
  background: #dc2626;
  color: #fff;
}

.card-body {
  flex: 1;
  min-width: 0;
}

.card-name {
  display: block;
  font-weight: 600;
  font-size: 13px;
  color: #1a202c;
  line-height: 1.3;
}

.card-desc {
  font-size: 11.5px;
  color: #718096;
  margin-top: 3px;
  line-height: 1.4;
}

.nav-item-yellow {
  background: #FBBF24;
  color: #1e3a5f !important;
}
.nav-item-yellow:hover, .nav-item-yellow.nav-item-active {
  background: #FCD34D !important;
  color: #1e3a5f !important;
}

.nav-item-red {
  background: #DC2626;
}
.nav-item-red:hover, .nav-item-red.nav-item-active {
  background: #B91C1C !important;
}

/* ── Sub-Structures dropdown in sidebar ───────────────────────────────────── */
.nav-item-dropdown-wrapper {
  position: relative;
  background: #FBBF24;
}
.nav-item-dropdown-wrapper:hover {
  background: #FCD34D;
}

.nav-chevron {
  margin-left: auto;
  font-size: 11px;
  opacity: 0.7;
  transition: transform 0.2s;
}

.sub-nav-dropdown {
  background: #0f3a63;
  border-top: 1px solid rgba(255,255,255,0.1);
  border-bottom: 1px solid rgba(255,255,255,0.1);
  overflow: hidden;
  padding: 4px 0;
}

.sub-nav-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 9px 12px;
  margin: 2px 4px;
  width: calc(100% - 8px);
  text-align: left;
  color: rgba(255,255,255,0.95);
  font-size: 14px;
  font-weight: 500;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;
  white-space: normal;
  line-height: 1.3;
}

.sub-nav-item:hover {
  background: rgba(255,255,255,0.15);
  color: #fff;
}

.sub-nav-icon {
  flex-shrink: 0;
  font-size: 11px;
  opacity: 0.6;
}

.sub-nav-name {
  flex: 1;
}

.sub-nav-abbr {
  font-size: 11px;
  opacity: 0.6;
  flex-shrink: 0;
}

/* ── Parent breadcrumb ────────────────────────────────────────────────────── */
.parent-breadcrumb {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px 5px 8px;
  margin-bottom: 12px;
  background: #f0f4ff;
  border: 1px solid #d0def8;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: #144A78;
  cursor: pointer;
  transition: background 0.15s, transform 0.15s;
  width: fit-content;
}

.parent-breadcrumb:hover {
  background: #dbeafe;
  transform: translateX(-2px);
}

.parent-breadcrumb i {
  font-size: 11px;
}

/* ── Sub-Structures grid ─────────────────────────────────────────────────── */
.sub-structures-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.sub-structure-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  cursor: pointer;
  transition: box-shadow 0.2s, transform 0.2s;
}

.sub-structure-card:hover {
  box-shadow: 0 6px 18px rgba(20,74,120,0.15);
  transform: translateY(-2px);
}

.sub-structure-icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #e8f0fe;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #144A78;
  font-size: 18px;
}

.sub-structure-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.sub-structure-name {
  font-weight: 700;
  font-size: 14px;
  color: #1a202c;
  line-height: 1.3;
}

.sub-structure-abbr {
  font-size: 12px;
  color: #718096;
}

.sub-structure-type-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  padding: 2px 7px;
  border-radius: 999px;
  width: fit-content;
  margin-top: 2px;
}

.badge-public {
  background: #dbeafe;
  color: #1d4ed8;
}

.badge-private {
  background: #d1fae5;
  color: #065f46;
}

.sub-structure-arrow {
  flex-shrink: 0;
  color: #a0aec0;
  font-size: 14px;
}
</style>